// Экран входа — по мотивам логин-паттерна AlignUI (template-finance, (auth)/login).
// Четыре состояния живут прямо в форме: ошибка показывает конверт сервера словами
// (message + next_action), виноватые поля подсвечиваются по fields; во время запроса
// кнопка не исчезает и не сереет молча — она гейтится с объяснением (правило 10).
// После входа — ?next= из адреса, затем адрес, с которого выкинуло на вход, затем
// стартовый маршрут по роли (утро финдиректора: ноль кликов до подотчёта).

import type { FormEvent } from 'react';
import { useState } from 'react';

import {
  RiErrorWarningLine,
  RiEyeLine,
  RiEyeOffLine,
  RiLoader4Line,
  RiLock2Line,
  RiUserLine,
} from '@remixicon/react';
import { useLocation, useNavigate } from 'react-router';

import { АДРЕС_ПОЛИТИКИ, АДРЕС_УСЛОВИЙ } from '@/shared/auth/документы';
import { useПлатформа } from '@/shared/desktop/где-мы';
import { login, loginDemo } from '@/shared/auth/session';
import { startRoute } from '@/shared/auth/start-route';
import { ApiError } from '@/shared/errors/api-error';

import { ServerAddressPanel } from './ServerAddressPanel';

/**
 * Показательный сервер: вымышленные люди и суммы, полоса «демо» поверх разделов.
 *
 * Адрес вшит здесь, а не приходит настройкой: это не сервер организации, а наша витрина, одна
 * на все сборки. Заказчик задаёт СВОЙ адрес полем ниже, и эта кнопка его не трогает.
 */
const АДРЕС_ДЕМОНСТРАЦИИ = 'https://bark-demo.uxcode.agency';
import * as Alert from '@/shared/ui/alert';
import barkMark from '@/shared/ui/brand/bark-mark.png';
import * as Divider from '@/shared/ui/divider';
import * as Hint from '@/shared/ui/hint';
import * as Input from '@/shared/ui/input';
import * as Button from '@/shared/ui/button';
import * as Label from '@/shared/ui/label';

/** Ошибки полей формы: login — имя входа, phrase — фраза для входа. */
interface FieldErrors {
  login?: string;
  phrase?: string;
}

/** Конверт сервера называет поля по контракту — сводим их к двум полям формы. */
function fieldErrorsFromEnvelope(fields: Record<string, string> | undefined): FieldErrors {
  if (fields === undefined) {
    return {};
  }
  const result: FieldErrors = {};
  for (const [name, message] of Object.entries(fields)) {
    if (name === 'login') {
      result.login = message;
    }
    if (name === 'password') {
      result.phrase = message;
    }
  }
  return result;
}

/**
 * Куда уходить после входа. Только внутренние адреса: чужой адрес в ?next= — это
 * подсунутая ссылка, на неё не уходим.
 */
function safeNext(raw: string | null): string | null {
  if (raw === null || !raw.startsWith('/') || raw.startsWith('//')) {
    return null;
  }
  return raw;
}

export function LoginPage() {
  const где = useПлатформа();
  const navigate = useNavigate();
  const location = useLocation();

  const [loginName, setLoginName] = useState('');
  const [phrase, setPhrase] = useState('');
  const [showPhrase, setShowPhrase] = useState(false);
  const [pending, setPending] = useState(false);
  const [serverError, setServerError] = useState<ApiError | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) {
      // Кнопка не заблокирована, но действие гейтится: запрос уже идёт, и это написано
      // на самой кнопке и строкой под ней. Второй запрос не отправляем.
      return;
    }

    const nextErrors: FieldErrors = {};
    if (loginName.trim() === '') {
      nextErrors.login = 'Введите имя входа';
    }
    if (phrase === '') {
      nextErrors.phrase = 'Введите фразу для входа';
    }
    if (nextErrors.login !== undefined || nextErrors.phrase !== undefined) {
      setFieldErrors(nextErrors);
      return;
    }

    setPending(true);
    setServerError(null);
    setFieldErrors({});
    try {
      const user = await login(loginName.trim(), phrase);

      const next = safeNext(new URLSearchParams(location.search).get('next'));
      const from = (location.state as { from?: { pathname: string; search: string } } | null)?.from;
      const fromPath = from === undefined ? null : `${from.pathname}${from.search}`;
      // '/' — честная страница «разделы не назначены», а не петля на вход.
      navigate(next ?? fromPath ?? startRoute(user) ?? '/', { replace: true });
    } catch (error) {
      const apiError = ApiError.is(error)
        ? error
        : new ApiError({
            code: 'unknown_error',
            message: 'вход не удался по неизвестной причине',
            next_action: 'повторите попытку; не помогло — сообщите администратору',
          });
      setServerError(apiError);
      setFieldErrors(fieldErrorsFromEnvelope(apiError.envelope.fields));
    } finally {
      setPending(false);
    }
  }

  // Панель адреса разворачивается при ЛЮБОМ отказе входа, а не только когда сервер молчит.
  //
  // До 21.09.2026 её открывал лишь обрыв связи. На планшете это давало готовый отказ магазина:
  // проверяющий, не сделавший шаг «смените сервер», отправлял демонстрационное имя на сервер
  // заказчика, получал «имя или фраза не подходят» — и панель, в которой и была причина,
  // оставалась свёрнутой серой строкой.
  const показатьАдрес = serverError !== null;

  async function открытьДемонстрацию() {
    setPending(true);
    setServerError(null);
    setFieldErrors({});
    try {
      const user = await loginDemo(АДРЕС_ДЕМОНСТРАЦИИ);
      navigate(startRoute(user) ?? '/', { replace: true });
    } catch (error) {
      setServerError(
        ApiError.is(error)
          ? error
          : new ApiError({
              code: 'unknown_error',
              message: 'демонстрация не открылась',
              next_action: 'повторите чуть позже; на вход по имени и фразе это не влияет',
            }),
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <main className='flex h-full min-h-full w-full items-center justify-center bg-bg-weak-50 p-4'>
      <div className='w-full max-w-md'>
        <form
          onSubmit={(event) => void handleSubmit(event)}
          noValidate
          className='flex w-full flex-col gap-6 rounded-20 bg-bg-white-0 p-5 shadow-regular-xs ring-1 ring-inset ring-stroke-soft-200 md:p-8'
        >
          <div className='flex flex-col items-center gap-2'>
            <div className='flex size-16 shrink-0 items-center justify-center rounded-full bg-bg-white-0 shadow-regular-xs ring-1 ring-inset ring-stroke-soft-200'>
              <img src={barkMark} alt='' className='h-8 w-auto' />
            </div>
            <div className='space-y-1 text-center'>
              <h1 className='text-title-h6 text-text-strong-950'>Барк</h1>
              <p className='text-paragraph-sm text-text-sub-600'>
                Войдите по имени и фразе, выданным администратором.
              </p>
            </div>
          </div>

          <Divider.Root />

          {serverError !== null && (
            <Alert.Root variant='light' status='error' size='small' role='alert'>
              <Alert.Icon as={RiErrorWarningLine} />
              <div className='flex min-w-0 flex-col gap-0.5'>
                <span>{serverError.envelope.message}</span>
                {serverError.envelope.next_action !== undefined && (
                  <span className='text-paragraph-xs opacity-80'>
                    {serverError.envelope.next_action}
                  </span>
                )}
              </div>
            </Alert.Root>
          )}

          <div className='flex flex-col gap-3'>
            <div className='flex flex-col gap-1'>
              <Label.Root htmlFor='login-name'>Имя входа</Label.Root>
              <Input.Root hasError={fieldErrors.login !== undefined}>
                <Input.Wrapper>
                  <Input.Icon as={RiUserLine} />
                  <Input.Input
                    id='login-name'
                    name='username'
                    type='text'
                    autoComplete='username'
                    // ⚠️ Клавиатура планшета сама поднимает первую букву и правит слово
                    // автозаменой: «findir» превращается в «Findir», а сервер сравнивает имя
                    // побайтно и отвечает «имя или фраза не подходят». Человек при этом видит
                    // ровно то, что набрал, и причину не угадает никогда. Найдено разбором
                    // перед подачей в магазин 19.09.2026.
                    autoCapitalize='none'
                    autoCorrect='off'
                    spellCheck={false}
                    autoFocus
                    value={loginName}
                    onChange={(event) => setLoginName(event.target.value)}
                    aria-invalid={fieldErrors.login !== undefined}
                  />
                </Input.Wrapper>
              </Input.Root>
              {fieldErrors.login !== undefined && (
                <Hint.Root hasError>
                  <Hint.Icon as={RiErrorWarningLine} />
                  {fieldErrors.login}
                </Hint.Root>
              )}
            </div>

            <div className='flex flex-col gap-1'>
              <Label.Root htmlFor='login-phrase'>Фраза для входа</Label.Root>
              <Input.Root hasError={fieldErrors.phrase !== undefined}>
                <Input.Wrapper>
                  <Input.Icon as={RiLock2Line} />
                  <Input.Input
                    id='login-phrase'
                    name='password'
                    type={showPhrase ? 'text' : 'password'}
                    autoComplete='current-password'
                    value={phrase}
                    onChange={(event) => setPhrase(event.target.value)}
                    aria-invalid={fieldErrors.phrase !== undefined}
                  />
                  <button
                    type='button'
                    onClick={() => setShowPhrase((current) => !current)}
                    aria-label={showPhrase ? 'Скрыть фразу' : 'Показать фразу'}
                  >
                    {showPhrase ? (
                      <RiEyeOffLine className='size-5 text-text-soft-400' />
                    ) : (
                      <RiEyeLine className='size-5 text-text-soft-400' />
                    )}
                  </button>
                </Input.Wrapper>
              </Input.Root>
              {fieldErrors.phrase !== undefined && (
                <Hint.Root hasError>
                  <Hint.Icon as={RiErrorWarningLine} />
                  {fieldErrors.phrase}
                </Hint.Root>
              )}
            </div>
          </div>

          {/* Адрес сервера настраивается ДО входа: пока он неверный, внутрь не попасть, а
              программа у заказчика ставится тем же установщиком, что и на демонстрации.
              Панель разворачивается сама, если сервер не ответил, — в девяти случаях из
              десяти дело именно в адресе. */}
          {/* На планшете панель тоже есть, но принимает ТОЛЬКО защищённый адрес: обращение
              по открытому протоколу система блокирует сама, и человек видит «нет связи» при
              живом сервере. До 11.09.2026 поле там прятали целиком — и это ломало две вещи
              сразу. Сборка в магазине одна, а серверов два: рабочий заказчика и
              показательный, в который пускают проверяющего из магазина; а заказчик, сменив
              сервер, не смог бы переехать без новой сборки. */}
          {где !== null && (
            <ServerAddressPanel развернуть={показатьАдрес} толькоЗащищённый={где === 'ipad'} />
          )}

          <div className='flex flex-col gap-2'>
            {/* Та же кнопка, что и во всём продукте. До 25.08.2026 здесь стояла парадная
                кнопка с градиентом — единственный градиент на весь интерфейс и единственное
                место, где она вообще применялась. Одна кнопка на продукт: человек, входящий
                каждое утро, не должен видеть на входе кнопку не из этой программы. */}
            <Button.Root type='submit' variant='primary' mode='filled' size='medium'>
              {pending && <Button.Icon as={RiLoader4Line} className='animate-spin' />}
              {pending ? 'Проверяем имя и фразу…' : 'Войти'}
            </Button.Root>
            {pending && (
              <p aria-live='polite' className='text-center text-paragraph-xs text-text-sub-600'>
                Запрос уже ушёл, ждём ответ сервера.
              </p>
            )}
            {/* Демонстрация: открыть кабинет с вымышленными людьми и суммами, не имея учётной
                записи. Нужна двум людям сразу — проверяющему из магазина, которому нельзя
                показывать деньги заказчика, и любому, кто хочет посмотреть продукт до
                подключения. До 21.09.2026 проверяющему приходилось руками вписывать адрес
                показательного сервера мелкой серой строкой, и любой промах на этом шаге
                означал «не смогли войти», то есть отказ.

                На рабочем столе заказчика кнопки нет: у него один сервер — свой. */}
            {(где === 'ipad' || где === 'браузер') && (
              <Button.Root
                type='button'
                variant='neutral'
                mode='stroke'
                size='medium'
                onClick={() => void открытьДемонстрацию()}
              >
                Посмотреть демонстрацию
              </Button.Root>
            )}
            {/* Дверей у человека две, и перепутать их легко: код приглашения открывает
                приложение на телефоне и в программу не пускает вовсе. Замер 13.09.2026:
                администратор, забывший фразу, дважды выдал себе код — и дважды не смог войти,
                потому что искал не ту дверь. Сказать об этом надо ЗДЕСЬ, на экране, где он
                стоит, а не в настройках, куда он не попадёт. */}
            <p className='text-center text-paragraph-xs text-text-sub-600'>
              Забыли фразу? Новую задаёт администратор в настройках программы. Код из приложения
              на телефоне сюда не подходит: в программу входят именем и фразой.
            </p>
          </div>

          {/* Политика и условия видны ДО входа, а не только внутри «Моего входа»: проверяющий
              магазина ищет их на первом экране (правило Apple 5.1.1), и человек, которому
              выдали вход, вправе прочитать их раньше, чем войдёт. На рабочем столе строки нет:
              программу там ставит сама организация, а магазина, который это спросит, нет. */}
          {(где === 'ipad' || где === 'браузер') && (
            <p className='flex flex-wrap justify-center gap-x-3 gap-y-1 text-paragraph-xs text-text-sub-600'>
              <a
                href={АДРЕС_ПОЛИТИКИ}
                target='_blank'
                rel='noreferrer'
                className='underline decoration-dotted underline-offset-2 transition duration-200 ease-out hover:text-text-strong-950 тач:inline-flex тач:min-h-11 тач:items-center'
              >
                Политика конфиденциальности
              </a>
              <a
                href={АДРЕС_УСЛОВИЙ}
                target='_blank'
                rel='noreferrer'
                className='underline decoration-dotted underline-offset-2 transition duration-200 ease-out hover:text-text-strong-950 тач:inline-flex тач:min-h-11 тач:items-center'
              >
                Условия использования
              </a>
            </p>
          )}
        </form>
      </div>
    </main>
  );
}
